<template>
    <component
        :is="tag"
        ref="targetEl"
        :style="tag === 'div' ? `min-height:${fixedMinHeight ? fixedMinHeight : minHeight}px` : null"
    >
        <slot v-if="shouldRender" />
    </component>
</template>

<script>
export default {
    props: {
        tag: {
            type: String,
            default: 'div'
        },
        renderOnIdle: Boolean,
        unrender: Boolean,
        minHeight: Number,
        unrenderDelay: {
            type: Number,
            default: 10000,
        },
        disableLazy: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            shouldRender: false,
            targetEl: null,
            fixedMinHeight: 0,
            unrenderTimer: null,
            renderTimer: null,
        };
    },
    mounted() {
        if (this.disableLazy) {
            this.shouldRender = true;
            return;
        }

        this.targetEl = this.$refs.targetEl;

        // Find the scroll container to use as root for IntersectionObserver
        const scrollContainer = this.findScrollContainer(this.targetEl);
        const root = scrollContainer === window ? null : scrollContainer;

        const options = {
            root: root,
            rootMargin: '200px',
        };

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                clearTimeout(this.unrenderTimer);
                this.renderTimer = setTimeout(() => {
                    this.shouldRender = true;
                    this.fixedMinHeight = 0;
                }, this.unrender ? 200 : 0);
                if (!this.unrender) {
                    observer.disconnect();
                }
            } else if (this.unrender) {
                clearTimeout(this.renderTimer);
                this.unrenderTimer = setTimeout(() => {
                    const scrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

                    // Get dimensions before unrendering
                    const clientHeight = this.targetEl.clientHeight || 0;
                    const scrollHeight = this.targetEl.scrollHeight || 0;

                    this.fixedMinHeight = Math.max(
                        this.minHeight || clientHeight,
                        scrollHeight
                    );

                    this.shouldRender = false;

                    // Restore scroll position if we found a scroll container
                    if (scrollContainer) {
                        this.$nextTick(() => {
                            scrollContainer.scrollTop = scrollTop;
                        });
                    }
                }, this.unrenderDelay);
            }
        }, options);

        observer.observe(this.targetEl);
        if (this.renderOnIdle) {
            this.onIdle(() => {
                this.shouldRender = true;
                if (!this.unrender) {
                    observer.disconnect();
                }
            });
        }
    },
    methods: {
        onIdle(cb = () => { }) {
            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(cb);
            } else {
                setTimeout(() => {
                    this.$nextTick(cb);
                }, 300);
            }
        },
        findScrollContainer(element) {
            if (!element || element === document.documentElement) {
                return window;
            }

            let parent = element.parentElement;
            while (parent) {
                const { overflow, overflowY } = window.getComputedStyle(parent);
                if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
                    return parent;
                }
                parent = parent.parentElement;
            }

            return window;
        },
    },
};
</script>
